import { normalizeMonitorTest } from '../lib/obd/extended.ts';
import type { MonitorTest, Readiness, VehicleInfo } from '../lib/obd/extended.ts';

// The three panels that turn a code reader into a scan tool: what the vehicle
// has finished self-testing, what those tests actually measured, and what
// software the ECU is running.

/**
 * Readiness monitors.
 *
 * This is the panel an inspection station looks at. A vehicle with no codes
 * still fails if its monitors have not completed, which happens for a hundred
 * miles or so after any battery disconnect or code clear.
 */
export function ReadinessPanel({ readiness }: { readiness: Readiness | null }) {
  if (!readiness) return null;

  const supported = readiness.monitors.filter((m) => m.supported);
  const continuous = supported.filter((m) => m.continuous);
  const periodic = supported.filter((m) => !m.continuous);

  return (
    <div className="card">
      <div className="row wrap" style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0, flex: 1 }}>Emissions readiness</h2>
        <span className="pill">{readiness.ignition === 'compression' ? 'compression ignition' : 'spark ignition'}</span>
        <span className={`pill ${readiness.ready ? 'ok' : 'warn'}`}>
          <i className="dot" />
          {readiness.ready ? 'ready to test' : `${readiness.incompleteCount} incomplete`}
        </span>
      </div>

      {periodic.length > 0 && (
        <>
          <span className="eyebrow">Periodic monitors</span>
          <div className="monitor-grid">
            {periodic.map((m) => (
              <MonitorChip key={m.name} name={m.name} complete={m.complete} />
            ))}
          </div>
        </>
      )}

      {continuous.length > 0 && (
        <>
          <span className="eyebrow" style={{ marginTop: 14 }}>
            Continuous monitors
          </span>
          <div className="monitor-grid">
            {continuous.map((m) => (
              <MonitorChip key={m.name} name={m.name} complete={m.complete} />
            ))}
          </div>
        </>
      )}

      <p className="small dim" style={{ marginBottom: 0, marginTop: 12 }}>
        {readiness.ready
          ? 'Every supported self-test has run since codes were last cleared, so the vehicle will not be rejected for incomplete monitors.'
          : 'Incomplete monitors usually mean codes were cleared or the battery was disconnected recently. They finish on their own after a drive cycle, which is typically 50 to 100 miles of mixed driving.'}
      </p>
    </div>
  );
}

function MonitorChip({ name, complete }: { name: string; complete: boolean }) {
  return (
    <div className={`monitor ${complete ? 'done' : 'pending'}`}>
      {/* State is carried by the mark and the word, never by colour alone. */}
      <span className="mark" aria-hidden="true">
        {complete ? '✓' : '•'}
      </span>
      <span className="name">{name}</span>
      <span className="state">{complete ? 'complete' : 'not ready'}</span>
    </div>
  );
}

/**
 * Mode 06 test results.
 *
 * A code tells you a system failed. These are the measurements behind that
 * verdict, which is how a marginal catalyst gets caught before it sets P0420.
 */
export function MonitorTestsPanel({
  tests,
  readiness,
}: {
  tests: (MonitorTest & { monitorLabel: string })[];
  readiness?: Readiness | null;
}) {
  if (tests.length === 0) return null;

  // Stored scans may predate some of these fields, so derive them before use.
  const normalized = tests.map((t) => normalizeMonitorTest(t) as MonitorTest & { monitorLabel: string });
  const judged = normalized.filter((t) => !t.invalidReason);
  const unusable = normalized.filter((t) => t.invalidReason);
  const failing = judged.filter((t) => t.pass === false);
  const anyUnscaled = judged.some((t) => !t.scaled);

  return (
    <div className="card">
      <div className="row wrap" style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0, flex: 1 }}>On-board test results</h2>
        <span className={`pill ${failing.length ? 'crit' : 'ok'}`}>
          <i className="dot" />
          {failing.length ? `${failing.length} outside limits` : 'all within limits'}
        </span>
        {unusable.length > 0 && <span className="pill">{unusable.length} unreadable</span>}
      </div>

      <div className="banner info small" style={{ marginBottom: 12 }}>
        <strong>Reading this panel.</strong> These are the vehicle's own self-test measurements, not fault codes. A value
        outside its limits is a lead to chase, not a confirmed failure: the same monitor must fail repeatedly before the
        ECU sets a code.
        {readiness && !readiness.ready && (
          <>
            {' '}
            {readiness.incompleteCount} monitor
            {readiness.incompleteCount === 1 ? ' has' : 's have'} not finished since codes were last cleared, so any
            result belonging to those is provisional.
          </>
        )}{' '}
        Manufacturers are free to define their own monitor and test ids beyond the standard set, so an unnamed monitor
        is normal rather than an error.
      </div>

      <div className="scroll-x">
        <table className="offers">
          <thead>
            <tr>
              <th>Monitor</th>
              <th className="n">Measured</th>
              <th className="n">Min</th>
              <th className="n">Max</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {judged.map((t, i) => (
              <tr key={`${t.monitorId}-${t.testId}-${i}`}>
                <td>
                  {t.monitorLabel}
                  <br />
                  <span className="dim small mono">
                    test 0x{t.testId.toString(16).toUpperCase().padStart(2, '0')}
                    {!t.scaled && ' · raw counts'}
                  </span>
                </td>
                <td className="n">
                  <strong>{t.value}</strong> {t.unit}
                </td>
                <td className="n dim">{t.min ?? '—'}</td>
                <td className="n dim">{t.max ?? '—'}</td>
                <td>
                  {t.pass === null ? (
                    <span className="dim small">no limits reported</span>
                  ) : (
                    <>
                      <span className={`pill ${t.pass ? 'ok' : 'crit'}`}>
                        <i className="dot" />
                        {t.pass ? 'within limits' : 'outside limits'}
                      </span>
                      {typeof t.margin === 'number' && Number.isFinite(t.margin) && (
                        <>
                          <br />
                          <span className="dim small">
                            {t.margin >= 0
                              ? `${Math.round(t.margin * 100)}% margin`
                              : `${Math.round(Math.abs(t.margin) * 100)}% past the limit`}
                          </span>
                        </>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {anyUnscaled && (
        <p className="small dim" style={{ marginBottom: 0, marginTop: 10 }}>
          Rows marked raw use a unit-and-scaling id outside our conversion table, so the numbers are shown unconverted.
          Within or outside limits stays correct regardless, because each value is compared against its own limits.
        </p>
      )}

      {unusable.length > 0 && (
        <details style={{ marginTop: 10 }}>
          <summary className="small dim">{unusable.length} records did not decode cleanly</summary>
          <p className="small dim" style={{ marginTop: 8, marginBottom: 8 }}>
            Each of these reported a lower limit above its upper limit, which is impossible, so the record framing is
            wrong rather than the vehicle being faulty. Mode 06 record layout varies by manufacturer beyond what the
            standard pins down. They are listed rather than hidden, and deliberately not counted as failures.
          </p>
          <div className="scroll-x">
            <table className="offers">
              <tbody>
                {unusable.map((t, i) => (
                  <tr key={`bad-${i}`}>
                    <td className="mono small">
                      MID 0x{t.monitorId.toString(16).toUpperCase().padStart(2, '0')} TID 0x
                      {t.testId.toString(16).toUpperCase().padStart(2, '0')}
                    </td>
                    <td className="n mono small">
                      {t.value} in [{t.min}, {t.max}]
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}

/**
 * Calibration identity.
 *
 * The CVN is a checksum over the ECU's software. A value that does not match
 * the factory calibration means the vehicle has been reflashed, which is worth
 * knowing before you diagnose anything else on a modified car.
 */
export function CalibrationPanel({ info, adapterVoltage }: { info: VehicleInfo | null; adapterVoltage: number | null }) {
  if (!info && adapterVoltage === null) return null;

  return (
    <div className="card">
      <h2>ECU identity</h2>
      <div className="spec-grid">
        {info?.ecuName && <Spec k="Module" v={info.ecuName} />}
        {info?.calibrationIds.map((id, i) => <Spec key={id} k={`Calibration ${i + 1}`} v={id} mono />)}
        {info?.calibrationVerificationNumbers.map((cvn, i) => (
          <Spec key={cvn} k={`CVN ${i + 1}`} v={cvn} mono />
        ))}
        {adapterVoltage !== null && <Spec k="Adapter voltage" v={`${adapterVoltage.toFixed(1)} V`} />}
      </div>
      {info?.calibrationVerificationNumbers.length ? (
        <p className="small dim" style={{ marginBottom: 0, marginTop: 10 }}>
          The verification number is a checksum of the module's software. Compare it against the factory value to tell a
          stock ECU from a reflashed one.
        </p>
      ) : null}
    </div>
  );
}

function Spec({ k, v, mono = false }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="spec">
      <span className="k">{k}</span>
      <span className={`v${mono ? ' mono' : ''}`} style={mono ? { fontSize: 13 } : undefined}>
        {v}
      </span>
    </div>
  );
}
